import { touchSlop } from '../_touchTarget.js'

// ChatToolRow — one entry of the assistant's live agentic timeline: the
// structured, human face of a tool the AI ran while answering. The transport
// upserts tool.activity frames onto the streaming message's `tools` array
// (simoneAiTransport._patchToolActivity); ChatMessage.Activity renders each as
// this row — so tool traffic never appears as raw "[Tool call] …" message text.
//
// Collapsed: status glyph (pulsing dot → check/cross) + a verb-phrase label
// ("Searching “transactions dashboard”") + duration + expand chevron.
// THE SERVER LABEL (2026-09-30 per Nika "make ai responses more user
// friendly"): the tool.activity frames and the saved tool_call part carry
// `label` — plain words, at most 80 characters, in the turn's language
// ("Showing the 5 biggest open deals"). A row that has one shows it as the
// step, and its argument hint leaves the collapsed row (the exact arguments
// stay one click away, in the expanded terminal). A row without one (an older
// server or saved turn) keeps the tool's words and its argument hint — each
// argument named in the person's words, never a value the step's words
// already say.
// Expanded (click): the exact input args and result summary in ToolTerminal
// blocks. Expansion state lives on root._aiToolOpen keyed by row id, so it
// survives the per-delta message-array churn while the turn streams.
//
// Every factory is SELF-CONTAINED (no module-scope helper refs) — the simone
// surface is consumed by frank/brender hosts where free vars inside serialized
// factories throw (see Chat.js `ck` note). What two factories share lives in
// this component's `scope` (the FA204 shape ChatMessage and ChatCard use).
export const ChatToolRow = {
  // THE ARGUMENT NAMES, ONCE (dev.my 2026-10-31, CHECK 8d: a Georgian
  // session's collapsed row read "title: Undo gone" while its open details
  // said "სათაური"). The collapsed hint (Head.Hint) and the open details
  // (ToolArgsList) name an argument with the SAME words, from this one
  // helper: the map, the snake / kebab → camel read and the humanized
  // fallback. A `scope` method, not a module-scope helper: the factories
  // that call it are serialized by frank, where a module read is undefined
  // (the FA204 trap); the method is self-contained for the same reason, and
  // a host without polyglot (docs, preview) reads the humanized name.
  scope: {
    toolArgName(el, key, tool) {
      const bare = String(tool || '').replace(/^module_fn_/, '')
      // THE ARGUMENT NAMES IN WORDS (dev 2026-10-30: a Georgian session's
      // step read "Ticket" — the tool's own key, in English; the Tasks
      // app's word is "task"). The common keys of the AI tools, after the
      // Tasks and Calendar AI forms' own field words; snake / kebab keys
      // read as camel (start_time → startTime). Every key a literal for the
      // i18n gate. A task's `body` IS its description (the server takes
      // `description` as its alias).
      const words = {
        ticket: 'ai.toolArg.task',
        ticketId: 'ai.toolArg.task',
        task: 'ai.toolArg.task',
        taskId: 'ai.toolArg.task',
        title: 'ai.toolArg.title',
        name: 'ai.toolArg.name',
        description: 'ai.toolArg.description',
        body: /_ticket$/.test(bare) ? 'ai.toolArg.description' : 'ai.toolArg.text',
        text: 'ai.toolArg.text',
        content: 'ai.toolArg.text',
        message: 'ai.toolArg.message',
        status: 'ai.toolArg.status',
        priority: 'ai.toolArg.priority',
        assignee: 'ai.toolArg.assignee',
        dueDate: 'ai.toolArg.dueDate',
        due: 'ai.toolArg.dueDate',
        type: 'ai.toolArg.type',
        issueType: 'ai.toolArg.type',
        column: 'ai.toolArg.column',
        event: 'ai.toolArg.event',
        eventId: 'ai.toolArg.event',
        date: 'ai.toolArg.date',
        start: 'ai.toolArg.start',
        startTime: 'ai.toolArg.start',
        end: 'ai.toolArg.end',
        endTime: 'ai.toolArg.end',
        timezone: 'ai.toolArg.timeZone',
        timeZone: 'ai.toolArg.timeZone',
        invitees: 'ai.toolArg.invitees',
        project: 'ai.toolArg.project',
        projectId: 'ai.toolArg.project',
        page: 'ai.toolArg.page',
        note: 'ai.toolArg.note',
        noteId: 'ai.toolArg.note',
        folder: 'ai.toolArg.folder',
        file: 'ai.toolArg.file',
        channel: 'ai.toolArg.channel',
        email: 'ai.toolArg.email',
        role: 'ai.toolArg.role',
        query: 'ai.toolArg.search',
        q: 'ai.toolArg.search',
        search: 'ai.toolArg.search',
        newName: 'ai.toolArg.newName',
        newTitle: 'ai.toolArg.newTitle',
        agent: 'ai.toolArg.agent',
        agentId: 'ai.toolArg.agent',
        theme: 'ai.toolArg.theme',
        summary: 'ai.toolArg.summary'
      }
      const camel = String(key).replace(/[_-]+([a-zA-Z0-9])/g, (m, c) => c.toUpperCase())
      const wordKey = Object.prototype.hasOwnProperty.call(words, camel) ? words[camel] : ''
      let said = ''
      if (wordKey) {
        try {
          said = el.call('polyglot', wordKey)
        } catch (_) {
          said = ''
        }
      }
      if (typeof said === 'string' && said && said !== wordKey) return said
      // A name the vocabulary does not hold reads humanized ("viewId" →
      // "View id"), as before.
      const plain = String(key)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase()
      return plain ? plain.charAt(0).toUpperCase() + plain.slice(1) : String(key)
    }
  },

  flow: 'y',
  width: '100%',
  minWidth: '0',
  // Entrance — each tool step slides in from the left as it starts, giving
  // the live reasoning timeline a visible cadence (one-shot at node creation;
  // status patches on the same keyed row never re-trigger it).
  animationName: 'fadeInLeft',
  animationDuration: '300ms',
  animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',

  Head: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    // The row WRAPS: where a narrow chat column (the app builder's, ~300px)
    // cannot hold the step's words beside Run / Run all / Skip, the controls
    // go to their own line under the words — never squeezing the label to one
    // letter per line (round 1b, ledger BLD-5: nino's and giorgi's Georgian
    // titles printed one letter per line beside the approval controls).
    flexWrap: 'wrap',
    gap: 'Z',
    align: 'center',
    width: '100%',
    minWidth: '0',
    padding: 'Y Z',
    // Clickable row header — same radius family as the Run/Skip/Open
    // controls below (`radiusControl`) rather than the spacing-letter
    // sequence: `Z` sits BELOW `A` in the shared letter alphabet
    // (`… W < X < Y < Z < A < B < C …`), so it does not even read as "small
    // A" — DESIGN_LANGUAGE.md §3's `old-letter-radius` lock retires this
    // whole letter-radius family in favor of the shape token table.
    round: 'radiusControl',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'B defaultBezier background',
    ':hover': { background: 'hover' },
    // A press steps the fill past the hover's: the brand's pressed wash
    // (`selected`), never opacity (Rule 65; the brand Interaction table). The
    // wash controls below take the same step.
    ':active': { background: 'selected' },
    // A finger's target: the 35px row takes the house tap slop (_touchTarget.js)
    // — UNDER its own controls. The slop is an absolute `:after`, painted over
    // the row's in-flow children, so every tap on Run / Run all / Open / Undo
    // hit this button and only toggled the details; Skip answered because it
    // overflowed past the row (round 1b, ledger BLD-5: giorgi's four taps on a
    // tablet). Its own stacking context with the pseudo at -1 (the
    // HomeTopSummary wash's recipe) keeps the 6px past the edge and hands every
    // tap on a control to the control.
    '@coarse': {
      position: 'relative',
      isolation: 'isolate',
      ':after': { ...touchSlop[':after'], zIndex: '-1' }
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = s.root
      if (!root || !root.update) return
      const open = { ...(root._aiToolOpen || {}) }
      open[s.id] = !open[s.id]
      root.update({ _aiToolOpen: open }, { preventFetch: true })
    },

    // Status glyph — a PERMANENT container so the glyph keeps its slot at the
    // row start: a status flip swaps the glyph inside the fixed box, and
    // nothing beside it moves. The variants inside are hide-gated: each stays
    // mounted, and a flip only toggles its `display`. (An `if:`-gated child
    // would not land at the row's end either: smbls inserts it at its
    // declared place, insertIfElementAtDeclaredOrder in element/src/core.js.)
    // Key-based auto-extend: Icon / Icon_1 (the `_N` suffix is ignored).
    PvStatus: {
      boxSize: 'Z',
      flexShrink: '0',
      flow: 'x',
      align: 'center center',
      PvRunDot: {
        hide: (el, s) => s.status !== 'running',
        boxSize: 'Y',
        round: '50%',
        background: 'blue',
        flexShrink: '0',
        animationName: 'blinking',
        animationDuration: '1100ms',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite'
      },
      // Awaiting the user's Run / Skip — the authorization gate paused this
      // call (simoneAiTransport runClientToolWithAuth). Amber, same pulse.
      PvAskDot: {
        hide: (el, s) => s.status !== 'approval',
        boxSize: 'Y',
        round: '50%',
        background: 'orange',
        flexShrink: '0',
        animationName: 'blinking',
        animationDuration: '1100ms',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite'
      },
      Icon: {
        hide: (el, s) => s.status !== 'done',
        name: 'checkmark',
        boxSize: 'Z',
        color: 'green',
        flexShrink: '0'
      },
      Icon_1: {
        hide: (el, s) => s.status !== 'error',
        name: 'x',
        boxSize: 'Z',
        color: 'red',
        flexShrink: '0'
      },
      // Denied — the user skipped it. Quiet, not red: declining an action the
      // gate asked about is a normal outcome, not a failure.
      Icon_2: {
        hide: (el, s) => s.status !== 'denied',
        name: 'x',
        boxSize: 'Z',
        color: 'caption',
        opacity: '.6',
        flexShrink: '0'
      }
    },

    Label: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '500',
      flexShrink: '0',
      // The server label is PRIMARY text (HOME_AMBIENT_SKIN_SPEC §3b rule 8):
      // up to 80 characters that wrap and are cut nowhere, at 390 too. It
      // takes the free width the hidden hint leaves (the duration and the
      // chevron keep the row's end, like every other row) and shrinks below
      // its text width to wrap instead of pushing the row; 1.5 is the house
      // body leading for Georgian descenders on a second line.
      // A FLOOR for the words (the spacing scale's `G`): below it the row wraps
      // the controls onto the next line instead of shrinking the label further
      // (BLD-5). Never wider than the line less the status glyph and its gap
      // (`B` here: the glyph and the gap are `Z` each in the row's own font,
      // the label is a step smaller), so the words keep the glyph's line and a
      // column narrower than the floor never overflows. The basis is the same
      // floor: a basis of the full text would start a line of its own.
      isLabelled: (el, s) => typeof s.label === 'string' && s.label.trim() !== '',
      '.isLabelled': {
        flex: '1 1 min(var(--spacing-G), calc(100% - var(--spacing-B)))',
        minWidth: 'min(var(--spacing-G), calc(100% - var(--spacing-B)))',
        overflowWrap: 'anywhere',
        lineHeight: '1.5'
      },
      color: (el, s) =>
        s.status === 'running' || s.status === 'approval' || (s.status === 'done' && s.created)
          ? 'title'
          : 'caption',
      opacity: (el, s) => (s.status === 'denied' ? '.55' : s.status === 'running' ? '.9' : '1'),
      text: (el, s) => {
        // The server's words are the row — already in the turn's language —
        // except for a DONE write that names what it made (the created phrase
        // below). Read before any branch, so the factory tracks the key from
        // the first render.
        const label = typeof s.label === 'string' ? s.label.trim() : ''
        const status = s.status
        const answer = typeof s.answer === 'string' ? s.answer.trim() : ''
        // Verb-phrase label per tool — inline (self-contained) so it survives
        // frank serialization in brender hosts. Every phrase is a language key
        // (persona round 3, B8: a step with no server label printed English
        // words — "Building an extension" — on a Georgian page), read in the
        // active language with its `{hole}`s filled. The server sends a label
        // on every frame now, so this is the fallback for an older server or
        // a saved turn.
        const t = (key, fill) => {
          let raw = ''
          try {
            raw = el.call('polyglot', key)
          } catch (_) {
            raw = ''
          }
          let str = typeof raw === 'string' && raw && raw !== key ? raw : ''
          if (str && fill) {
            for (const hole of Object.keys(fill)) str = str.split(hole).join(fill[hole])
          }
          return str
        }
        const tool = String(s.tool || 'tool')
        // Module fns arrive as module_fn_<app><Verb…> from the server frame —
        // strip the transport prefix so the label map and the humanizer see
        // the real fn name (the raw form rendered as "Module fn excalidraw
        // clear").
        const bare = tool.replace(/^module_fn_/, '')
        const a = s.args || {}
        const q = (v, n = 48) => {
          const str = String(v == null ? '' : v).trim()
          return str.length > n ? `${str.slice(0, n)}…` : str
        }
        // AI-301 (tickets/ai.md) — a COMPLETED write names what it made, not
        // what it was doing: "Created note “Q3 plan”" beats "Creating a note".
        // It wins over the server's label too: that label is the step in
        // progress ("Creating the task “…”"), and a done task row kept it
        // (wave 2, dev 2026-10-28). The phrase is in the active language (B8).
        // The structured outcome rides s.created — stamped live by the
        // transport from the client tool's own return value, restored on
        // reload by sessions._toChatMessages. The kind is a word of the
        // active language too (an unknown kind reads "item").
        const c = s.created
        if (status === 'done' && c && typeof c === 'object') {
          const kindKey =
            {
              note: 'ai.toolKind.note',
              document: 'ai.toolKind.document',
              ticket: 'ai.toolKind.ticket',
              channel: 'ai.toolKind.channel',
              folder: 'ai.toolKind.folder',
              project: 'ai.toolKind.project',
              agent: 'ai.toolKind.agent',
              event: 'ai.toolKind.event'
            }[String(c.kind || '')] || 'ai.toolKind.item'
          const kind = t(kindKey) || String(c.kind || 'item')
          const title = q(c.title)
          const made = title
            ? t('ai.toolStep.createdNamed', { '{kind}': kind, '{title}': title })
            : t('ai.toolStep.created', { '{kind}': kind })
          if (made) return made
        }
        // AN ANSWERED QUESTION SAYS WHAT WAS CHOSEN (W7 live, 2026-10-28: an
        // ask_user answered "No, cancel" still read "Waiting for your choice").
        // The server's ask_user label is the WAIT. Once the person picks, the
        // row names the pick (`answer` — the transport stamps it, a reload
        // restores it from the saved result); a question a new message closed
        // reads as unanswered. A failed one keeps the server's words.
        // A confirmation (confirm_with_user) asks on the same in-chat card and
        // gets the same `answer` stamp (F3, dev 2026-10-28: its row still read
        // "Waiting for your confirmation" after the answer).
        // A question its turn left (`denied`: a stop, a failed or ended turn —
        // the transport's _endWaitingCards; a reload of its not-run result)
        // reads as unanswered too, from the key: the label the transport wrote
        // then is in the language of that moment.
        // The answer is said ONCE (coordinator W3a): the row names it only
        // whole, up to 48 characters, and the card under the row then drops
        // it from its folded line (ChatMessage.scope.rowNamesPick, the same
        // limit). A longer answer — the person's own words — reads "You
        // answered" here, and its card shows the words.
        if (
          bare === 'ask_user' ||
          bare === 'askUser' ||
          bare === 'confirm_with_user' ||
          bare === 'confirmWithUser'
        ) {
          const said =
            status === 'done'
              ? answer && answer.length <= 48
                ? t('ai.toolStep.chose', { '{choice}': answer })
                : t('ai.toolStep.answered')
              : status === 'superseded' || status === 'denied'
                ? t('ai.toolStep.askUnanswered')
                : ''
          if (said) return said
        }
        if (label) return label
        // [key, holes] per tool — each hole named as written in the bundle
        // ('{query}'); a phrase with a hole only when the argument is there.
        const keys = {
          open_search: a.query
            ? ['ai.toolStep.searchFor', { '{query}': q(a.query) }]
            : ['ai.toolStep.search'],
          navigate_to_app: a.route
            ? ['ai.toolStep.openRoute', { '{route}': q(a.route) }]
            : ['ai.toolStep.navigate'],
          open_page_in_split: a.route
            ? ['ai.toolStep.openBeside', { '{route}': q(a.route) }]
            : ['ai.toolStep.closeSplit'],
          render_answer_ui: a.title
            ? ['ai.toolStep.drawAnswerNamed', { '{title}': q(a.title) }]
            : ['ai.toolStep.drawAnswer'],
          select_page_item: a.type
            ? ['ai.toolStep.openType', { '{type}': q(a.type) }]
            : ['ai.toolStep.openItem'],
          set_theme: a.theme
            ? ['ai.toolStep.switchThemeTo', { '{theme}': q(a.theme) }]
            : ['ai.toolStep.switchTheme'],
          confirm_with_user: ['ai.toolStep.confirm'],
          ask_user: ['ai.toolStep.choose'],
          close_modals: ['ai.toolStep.closeDialogs'],
          render_cards: ['ai.toolStep.showCards'],
          list_projects: ['ai.toolStep.readProjects'],
          list_workspace_records: a.collection
            ? ['ai.toolStep.readRecords', { '{collection}': q(a.collection) }]
            : ['ai.toolStep.listCollections'],
          list_deployments: ['ai.toolStep.checkDeployments'],
          list_builds: a.buildId ? ['ai.toolStep.readBuildLog'] : ['ai.toolStep.readBuilds'],
          list_project_environments: ['ai.toolStep.readEnvironments'],
          get_project: ['ai.toolStep.readProject'],
          create_project: a.name
            ? ['ai.toolStep.createProjectNamed', { '{name}': q(a.name) }]
            : ['ai.toolStep.createProject'],
          update_project: ['ai.toolStep.updateProject'],
          commit_project_changes: ['ai.toolStep.commitChanges'],
          create_extension: a.name
            ? ['ai.toolStep.buildExtensionNamed', { '{name}': q(a.name) }]
            : ['ai.toolStep.buildExtension'],
          create_app: ['ai.toolStep.createApp'],
          update_app: ['ai.toolStep.updateApp'],
          list_members: ['ai.toolStep.readMembers'],
          invite_member: a.email
            ? ['ai.toolStep.inviteEmail', { '{email}': q(a.email) }]
            : ['ai.toolStep.inviteMember'],
          list_invitations: ['ai.toolStep.readInvitations'],
          cancel_invitation: ['ai.toolStep.cancelInvitation'],
          update_member_role: ['ai.toolStep.updateRole'],
          remove_member: ['ai.toolStep.removeMember'],
          rename_workspace: ['ai.toolStep.renameWorkspace'],
          list_agents: ['ai.toolStep.readAgents'],
          create_agent: a.name
            ? ['ai.toolStep.createAgentNamed', { '{name}': q(a.name) }]
            : ['ai.toolStep.createAgent'],
          update_agent: ['ai.toolStep.updateAgent'],
          delegate_tasks: ['ai.toolStep.delegate'],
          chain_agents: ['ai.toolStep.chainAgents'],
          create_ticket: a.title
            ? ['ai.toolStep.createTicketNamed', { '{title}': q(a.title) }]
            : ['ai.toolStep.createTicket'],
          update_ticket: ['ai.toolStep.updateTicket'],
          assign_ticket: a.email
            ? ['ai.toolStep.assignTo', { '{email}': q(a.email) }]
            : ['ai.toolStep.clearAssignee'],
          move_ticket: ['ai.toolStep.moveTicket'],
          delete_ticket: ['ai.toolStep.deleteTicket'],
          propose_action: a.summary
            ? ['ai.toolStep.proposeSummary', { '{summary}': q(a.summary) }]
            : ['ai.toolStep.propose'],
          // An installed app's own action (the server name and the el.call
          // name): the act, never its key — "actionKey: bsDraftOrder" (dev
          // 2026-10-28). The approval card under the row names the change.
          propose_app_action: ['ai.toolStep.propose'],
          proposeModuleAction: ['ai.toolStep.propose'],
          create_folder: ['ai.toolStep.createFolder'],
          create_note: a.title
            ? ['ai.toolStep.createNoteNamed', { '{title}': q(a.title) }]
            : ['ai.toolStep.createNote'],
          create_channel: ['ai.toolStep.createChannel'],
          send_message: ['ai.toolStep.sendMessage'],
          // Module apps (both the server snake name and the el.call camel name
          // — frames carry one, dispatch data the other).
          excalidraw_get_scene: ['ai.toolStep.readDrawing'],
          excalidrawGetScene: ['ai.toolStep.readDrawing'],
          excalidraw_add_elements: ['ai.toolStep.addDrawing'],
          excalidrawAddElements: ['ai.toolStep.addDrawing'],
          excalidraw_update_elements: ['ai.toolStep.editDrawing'],
          excalidrawUpdateElements: ['ai.toolStep.editDrawing'],
          excalidraw_delete_elements: ['ai.toolStep.eraseDrawing'],
          excalidrawDeleteElements: ['ai.toolStep.eraseDrawing'],
          excalidraw_clear: ['ai.toolStep.clearDrawing'],
          excalidrawClear: ['ai.toolStep.clearDrawing']
        }
        const hit = Object.prototype.hasOwnProperty.call(keys, bare) ? keys[bare] : null
        const said = hit ? t(hit[0], hit[1]) : ''
        if (said) return said
        // A tool with no phrase of its own: the plain step words of the
        // active language. Only a host with no language bundle at all falls
        // to the tool's own name — humanized, snake and camel (module fns
        // arrive camelCased: excalidrawAddElements).
        const generic = t('ai.toolStep.generic')
        if (generic) return generic
        const words = bare
          .replace(/[_-]+/g, ' ')
          .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          .toLowerCase()
          .trim()
        return words ? words[0].toUpperCase() + words.slice(1) : 'Tool'
      }
    },

    // Compact args hint after the label — first couple of arg values, clipped.
    // Secondary meta: the one line may be cut, so the tooltip carries the
    // arguments whole (capped — a payload can be a whole document) — the
    // overflow law's condition for an ellipsis (§3b rule 8). Only on a row
    // WITHOUT a server label: a labelled row says the step in words, and its
    // exact arguments stay in the expanded terminal.
    // In the person's words (dev.my 2026-10-31, CHECK 8d: a Georgian row read
    // "შეიქმნა: შენიშვნა „Undo gone“" and beside it "title: Undo gone"): each
    // argument is named as the open details name it (scope.toolArgName — one
    // map), and a value the step's words already show is not said twice.
    Hint: {
      hide: (el, s) => typeof s.label === 'string' && s.label.trim() !== '',
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      opacity: '.65',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flex: '1 1 auto',
      minWidth: '0',
      title: (el, s) => {
        const a = s.args
        if (!a || typeof a !== 'object') return null
        let str = ''
        try {
          str = JSON.stringify(a)
        } catch (_) {
          return null
        }
        if (!str || str === '{}' || str === '[]') return null
        return str.length > 400 ? str.slice(0, 400) + '…' : str
      },
      text: (el, s) => {
        const a = s.args
        const tool = s.tool
        if (!a || typeof a !== 'object') return ''
        // Positional args (module fns): the whole payload is an array, or a
        // named bag whose only key is the positional `args` array — the
        // key-walk below yields nothing for those, so preview them directly.
        const preview = (v) => {
          const str = JSON.stringify(v)
          return str && str.length > 56 ? `${str.slice(0, 56)}…` : str || ''
        }
        if (Array.isArray(a)) return preview(a)
        if (Array.isArray(a.args)) {
          const inner = Array.isArray(a.args[0]) ? a.args[0] : a.args
          if (!inner.length) return ''
          // the count in the active language (B8 — never an English "items")
          const key = inner.length === 1 ? 'ai.toolStep.oneItem' : 'ai.tool.items'
          let raw = ''
          try {
            raw = el.call('polyglot', key)
          } catch (_) {
            raw = ''
          }
          const word = typeof raw === 'string' && raw && raw !== key ? raw : ''
          return word ? word.replace('{count}', String(inner.length)) : String(inner.length)
        }
        // Never an id (dev 2026-10-29: "access: private · projectId: 6aa332f5…"):
        // a database id or a uuid is a code name, not the person's words — the
        // tooltip and the expanded terminal keep the whole arguments.
        const isId = (str) =>
          /^[0-9a-f]{24}$/i.test(str) || /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(str)
        // SAID ONCE (the CHECK 8d row): a value the step's words show — the
        // item a done step made ("შეიქმნა: შენიშვნა „Undo gone“"), a phrase's
        // own value ("Searching “q3 plan”") — is not said twice; the rest of
        // the arguments stay. The words come from the Label's own factory
        // (the one ChatToolStepLabel reuses), read through the element tree:
        // the hint never disagrees with the step it sits beside, and this
        // factory stays self-contained for frank.
        const step = el.parent && el.parent.Label
        const stepWords = (row) => {
          let said = ''
          try {
            said = step && typeof step.text === 'function' ? step.text(step, row) : ''
          } catch (_) {
            said = ''
          }
          return typeof said === 'string' ? said : ''
        }
        // Whole: both edges next to a non-letter or the words' own ends (a
        // value the hint cuts, by its start alone).
        const holds = (words, part, cut) => {
          for (let at = words.indexOf(part); at >= 0; at = words.indexOf(part, at + 1)) {
            if (/[\p{L}\p{N}]/u.test(words.charAt(at - 1))) continue
            if (!cut && /[\p{L}\p{N}]/u.test(words.charAt(at + part.length))) continue
            return true
          }
          return false
        }
        // The step's words for the row with one value taken out of its data
        // (the arguments, the item it made): words that still hold the value
        // hold it as a word of the phrase or of a longer value ("Updating a
        // task" beside a type "task"), so that argument is not said yet.
        const drop = (o, str) => {
          if (!o || typeof o !== 'object' || Array.isArray(o)) return o
          const kept = {}
          for (const k of Object.keys(o)) {
            const v = o[k]
            if (v == null || typeof v === 'object' || String(v).trim() !== str) kept[k] = v
          }
          return kept
        }
        const words = stepWords(s)
        const parts = []
        for (const key of Object.keys(a)) {
          const v = a[key]
          if (v == null || typeof v === 'object') continue
          const str = String(v).trim()
          if (!str || isId(str)) continue
          // the part of the value this hint shows: its first 40 characters
          const part = str.slice(0, 40)
          const cut = part.length < str.length
          if (holds(words, part, cut)) {
            const rest = { ...s, args: drop(a, str), created: drop(s.created, str) }
            if (!holds(stepWords(rest), part, cut)) continue
          }
          parts.push(`${el.scope.toolArgName(el, key, tool)}: ${cut ? `${part}…` : str}`)
          if (parts.length >= 2) break
        }
        return parts.join(' · ')
      }
    },

    // AI-301 — the created item as a LIVE LINK on its own row: one click opens
    // the note/document/ticket/channel via the house deep-open path
    // (selectPageItem — the same el.call the select_page_item tool uses;
    // off-page fetch + navigation included). Only kinds with a canonical open
    // path get the affordance; the kind→type map is INLINE (self-contained)
    // so it survives frank serialization in brender hosts. Guarded — docs/
    // preview mount this row without the workspace function barrel, where the
    // call is unavailable and the button no-ops instead of throwing.
    OpenCreated: {
      // AI-306 — an undone item has nothing to open; the pair of affordances
      // collapses to the quiet "undone" tag below.
      // FA515: the hide reads s.root._aiUndone only after its other tests
      // pass. Each of those tests reads a state key today, so the factory
      // runs again (and subscribes) when one changes; the declaration keeps
      // the subscription from resting on that — a later test that reads no
      // signal (an el.call, a plain field) would drop it.
      hideDeps: ['_aiUndone'],
      hide: (el, s) =>
        s.status !== 'done' ||
        !s.created ||
        !['note', 'document', 'ticket', 'channel', 'event'].includes(
          String(s.created.kind || '')
        ) ||
        !!(s.root && s.root._aiUndone && s.created.id && s.root._aiUndone[String(s.created.id)]),
      tag: 'button',
      type: 'button',
      flow: 'x',
      align: 'center',
      gap: 'X',
      flexShrink: '0',
      padding: 'X Z',
      round: 'radiusPill',
      border: 'none',
      background: 'transparent',
      // Semantic ink pair (`['blueDeep', 'blueBright']`, DESIGN_SYSTEM.md
      // "raw-hue-ink" lock / symbols-mcp Rule 69), not the raw `blue` hue —
      // this is text ink, so it needs a light/dark pair, not a single fixed
      // value tuned to one ground.
      color: 'accentInk',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'B defaultBezier background',
      ':hover': { background: 'hover' },
      ':active': { background: 'selected' },
      // A finger's target of its own (touchRow — a control among neighbours),
      // like Run / Skip below.
      '@coarse': { minHeight: 'controlH', minWidth: 'controlH' },
      Label: { tag: 'span', text: '{{ common.action.open | polyglot }}' },
      Icon: { name: 'arrowRight', fontSize: 'Z2', flexShrink: '0' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const c = s.created
        if (!c || !c.id) return
        const type = {
          note: 'note',
          document: 'document',
          ticket: 'ticket',
          channel: 'channel',
          event: 'event'
        }[String(c.kind || '')]
        if (!type) return
        try {
          el.call('selectPageItem', type, String(c.id))
        } catch (_) {
          /* host without the workspace fn barrel — affordance no-ops */
        }
      }
    },

    // AI-306 (tickets/ai.md) — one-click Undo for an AI write, beside the
    // Open pill on the row that made it. Session-scoped: the latch lives on
    // root state (`_aiUndone`, keyed by created id), so it survives the
    // per-delta message churn but not a reload — matching the ticket's
    // scope. The delete paths are the EXISTING registered ops
    // (aiDeleteNote resolves notes AND documents by id; aiDeleteTicket) —
    // no new deletion capability, the same guarded el.call idiom as every
    // sibling. Channels are deliberately excluded: no aiDeleteChannel op
    // exists, and Undo must never gain destructive reach the AI itself
    // does not have. Guarded — hosts without the workspace fn barrel no-op.
    UndoCreated: {
      // read behind a conditional — declared so the factory re-fires (FA515;
      // see OpenCreated)
      hideDeps: ['_aiUndone'],
      hide: (el, s) =>
        s.status !== 'done' ||
        !s.created ||
        !['note', 'document', 'ticket', 'event'].includes(String(s.created.kind || '')) ||
        !!(s.root && s.root._aiUndone && s.created.id && s.root._aiUndone[String(s.created.id)]),
      tag: 'button',
      type: 'button',
      flexShrink: '0',
      padding: 'X Z',
      round: 'radiusPill',
      border: 'none',
      background: 'transparent',
      color: 'caption',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'B defaultBezier background, color',
      ':hover': { background: 'hover', color: 'title' },
      ':active': { background: 'selected', color: 'title' },
      '@coarse': { minHeight: 'controlH', minWidth: 'controlH' },
      // After a failed Undo the same button offers the retry (UndoFailure
      // below says why).
      Label: {
        tag: 'span',
        // read behind a conditional — declared so the factory re-fires (FA515)
        hideDeps: ['_aiUndoError'],
        hide: (el, s) =>
          !!(s.created && s.root && s.root._aiUndoError && s.root._aiUndoError[s.created.id]),
        text: '{{ ai.tool.undo | polyglot }}'
      },
      RetryLabel: {
        tag: 'span',
        hideDeps: ['_aiUndoError'],
        hide: (el, s) =>
          !(s.created && s.root && s.root._aiUndoError && s.root._aiUndoError[s.created.id]),
        text: '{{ ai.tool.undoRetry | polyglot }}'
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const c = s.created
        if (!c || !c.id) return
        const kind = String(c.kind || '')
        if (!['note', 'document', 'ticket', 'event'].includes(kind)) return
        // One delete at a time: a second click while it runs sends nothing.
        if (el._undoBusy) return
        const id = String(c.id)
        const root = s.root
        // A failed Undo stays on the row (`_aiUndoError`, keyed by the created
        // id, the `_aiUndone` latch's twin) until a retry works. It keeps the
        // CAUSE CODE aiUndoCreated classified, never the raw error: the row
        // says the cause in the active language (UndoFailure below).
        const noteFailure = (cause) => {
          if (!root || !root.update) return
          const was = root._aiUndoError || {}
          if (cause === null && !(id in was)) return
          const next = { ...was }
          if (cause === null) delete next[id]
          else next[id] = { cause: String(cause || '') }
          root.update({ _aiUndoError: next }, { preventFetch: true })
        }
        let pending
        try {
          // aiUndoCreated deletes BY ID (no page-state name-resolve — the
          // arrays are empty off their routes, and a name-resolve failure
          // here once looked like success). The latch is EVIDENCE-based:
          // only an explicit ok result marks the row undone.
          pending = el.call('aiUndoCreated', kind, id)
        } catch (_) {
          return /* host without the workspace fn barrel — affordance no-ops */
        }
        // A host without the op answers undefined: nothing ran, nothing to say.
        if (pending === undefined) return
        el._undoBusy = true
        Promise.resolve(pending)
          .then(
            (res) => {
              if (!res || res.ok === false) return noteFailure((res && res.cause) || '')
              noteFailure(null)
              if (!root || !root.update) return
              root.update(
                { _aiUndone: { ...(root._aiUndone || {}), [id]: true } },
                { preventFetch: true }
              )
            },
            (err) => {
              // The raw error is for the console; the row says the fallback.
              console.warn('[ChatToolRow] Undo failed:', err)
              noteFailure('')
            }
          )
          .then(() => {
            el._undoBusy = false
          })
      }
    },
    // Quiet outcome tag once undone — same slot/voice as SkippedTag below.
    UndoneTag: {
      // read behind a conditional — declared so the factory re-fires (FA515;
      // see OpenCreated)
      hideDeps: ['_aiUndone'],
      hide: (el, s) =>
        !(
          s.status === 'done' &&
          s.created &&
          s.created.id &&
          s.root &&
          s.root._aiUndone &&
          s.root._aiUndone[String(s.created.id)]
        ),
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      opacity: '.6',
      flexShrink: '0',
      fontStyle: 'italic',
      text: '{{ ai.tool.undone | polyglot }}'
    },
    // A step a new message closed while it waited on Run / Skip (the
    // transport's supersedeParkedAiToolApprovals; history: reason
    // `new_message`). Not the person's "skipped": they wrote instead. "Run it
    // now" asks the AI again as their follow-up (aiToolRunNow) — the server
    // already closed the call, so nothing runs in place. Once per row. A
    // question's card under its step leaves this reason to the row (ChatCard
    // PvWhy), so a question row keeps the tag (see SkippedTag).
    SupersededTag: {
      hide: (el, s) => s.status !== 'superseded',
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      opacity: '.6',
      flexShrink: '1',
      fontStyle: 'italic',
      text: '{{ ai.tool.superseded | polyglot }}'
    },
    // A question (ask_user) or a confirmation (confirm_with_user) is answered,
    // never run: its superseded row has no "Run it now" — the step would read
    // "Waiting for your choice". The names are inline (self-contained).
    RunNow: {
      hide: (el, s) =>
        s.status !== 'superseded' ||
        !!s.rerun ||
        ['ask_user', 'askUser', 'confirm_with_user', 'confirmWithUser'].includes(
          String(s.tool || '')
        ),
      tag: 'button',
      type: 'button',
      flexShrink: '0',
      padding: 'X Z',
      round: 'radiusPill',
      border: 'none',
      background: 'transparent',
      color: 'accentInk',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'B defaultBezier background',
      ':hover': { background: 'hover' },
      ':active': { background: 'selected' },
      '@coarse': { minHeight: 'controlH', minWidth: 'controlH' },
      text: '{{ ai.tool.runNow | polyglot }}',
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        try {
          el.call('aiToolRunNow', s.id)
        } catch (_) {
          /* host without the workspace fn barrel — affordance no-ops */
        }
      }
    },

    // Run / Skip — the authorization ask, inline on the row it concerns.
    // Buttons settle the transport's suspended Promise via
    // el.call('resolveAiToolApproval'); stopPropagation so answering never
    // also toggles the row. Guarded — in hosts without the workspace function
    // barrel (chrome-connect panel) the call is simply unavailable and the
    // buttons no-op instead of throwing into the click handler.
    Approve: {
      hide: (el, s) => s.status !== 'approval',
      tag: 'button',
      type: 'button',
      text: '{{ ai.tool.run | polyglot }}',
      flexShrink: '0',
      padding: 'Y2 Z1',
      round: 'radiusControl',
      border: 'none',
      // Run is the row's primary action: the brand's solid ink (a primary
      // action is never a hue fill), with the theme's own hover, focus and
      // press steps in both themes. No fill, ink or hover is declared here:
      // a declared prop outvotes the `theme:` compound (smbls f6757537d).
      theme: 'primary',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'B defaultBezier background',
      // Each control answers a finger at the 44pt floor itself (touchRow: a
      // control among neighbours draws the floor, no pseudo to take a
      // neighbour's edge) — on a tablet these three sat under the row's slop
      // and never received a tap (BLD-5).
      '@coarse': { minHeight: 'controlH', minWidth: 'controlH' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        try {
          el.call('resolveAiToolApproval', s.id, true)
        } catch (_) {}
      }
    },
    // AI-307 (tickets/ai.md) — "Approve all for this run": a 16-step run must
    // not mean 16 Run clicks, and Auto as the only alternative is too broad.
    // One click grants THE CURRENT TURN only — the transport keys the grant
    // to the live assistant id (approveAllAiToolsForTurn), so it expires with
    // the turn and never persists. The fn also approves this very card's
    // parked ask, so the click that grants the run unblocks it too. Guarded —
    // hosts without the workspace fn barrel no-op.
    ApproveAll: {
      hide: (el, s) => s.status !== 'approval',
      tag: 'button',
      type: 'button',
      text: '{{ ai.tool.runAll | polyglot }}',
      flexShrink: '0',
      padding: 'Y2 Z1',
      round: 'radiusControl',
      border: 'none',
      background: 'transparent',
      color: 'caption',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'B defaultBezier background, color',
      ':hover': { background: 'hover', color: 'title' },
      ':active': { background: 'selected', color: 'title' },
      title: (el) => el.call('polyglot', 'ai.tool.runAllTooltip'),
      '@coarse': { minHeight: 'controlH', minWidth: 'controlH' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        try {
          el.call('approveAllAiToolsForTurn')
        } catch (_) {}
      }
    },
    Deny: {
      hide: (el, s) => s.status !== 'approval',
      tag: 'button',
      type: 'button',
      text: '{{ ai.tool.skip | polyglot }}',
      flexShrink: '0',
      padding: 'Y2 Z1',
      round: 'radiusControl',
      border: 'none',
      background: 'transparent',
      color: 'caption',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'B defaultBezier background',
      ':hover': { background: 'hover', color: 'title' },
      ':active': { background: 'selected', color: 'title' },
      '@coarse': { minHeight: 'controlH', minWidth: 'controlH' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        try {
          el.call('resolveAiToolApproval', s.id, false)
        } catch (_) {}
      }
    },
    // Quiet outcome tag for a skipped step — same slot the duration uses.
    // A row says why it closed ONCE: a question its turn left (a stop, a
    // failed or ended turn) has a card under it that says why (ChatCard
    // PvWhy, `_closedBy`), so the row drops the tag — nobody pressed Skip. A
    // row with no such card (a step skipped on Run / Skip) keeps it. The
    // same rule keeps SupersededTag above: a card a new message closed
    // leaves the reason to its row. `_asks` is read first, so the factory
    // tracks it from the first run.
    SkippedTag: {
      hide: (el, s) => {
        const asks = Array.isArray(s._asks) ? s._asks : []
        return s.status !== 'denied' || asks.some((c) => !!c && !!c._closedBy)
      },
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      opacity: '.6',
      flexShrink: '0',
      fontStyle: 'italic',
      text: '{{ ai.tool.skipped | polyglot }}'
    },

    Took: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      opacity: '.6',
      flexShrink: '0',
      text: (el, s) => {
        if (!s.doneTs || !s.ts) return ''
        const secs = Math.max(0, s.doneTs - s.ts) / 1000
        return secs >= 0.1 ? `${secs.toFixed(1)}s` : ''
      }
    },

    Icon_2: {
      name: 'arrow angle down',
      boxSize: 'Z',
      color: 'caption',
      opacity: '.6',
      flexShrink: '0',
      transition: 'B defaultBezier transform',
      isOpen: (el, s) => !!(s.root && s.root._aiToolOpen && s.root._aiToolOpen[s.id]),
      '.isOpen': { transform: 'rotate(180deg)' }
    }
  },

  // WHY a step failed — the client tool's own reason (simoneAiTransport stamps
  // `failure` via toolFailure), or a server tool's refusal sentence. Keepz DEV
  // 2026-10-15: two refused render_answer_ui draws showed a bare ✗ and read as
  // an unexplained flake. Collapsed-row text, under the head; the red glyph
  // already says "failed", so the reason itself stays in the caption voice.
  // `hide`, not `if`: the line stays mounted, and a status flip only toggles
  // its `display`. (An `if:`-gated child would keep its place as well: smbls
  // inserts it at its declared place, not at the row's end; see PvStatus.)
  Failure: {
    hide: (el, s) =>
      s.status !== 'error' || !(typeof s.failure === 'string' && s.failure.trim() !== ''),
    tag: 'p',
    margin: '0',
    padding: 'X Z 0 B',
    fontSize: 'Z1',
    lineHeight: '1.4',
    color: 'caption',
    overflowWrap: 'anywhere',
    text: (el, s) => (typeof s.failure === 'string' ? s.failure : '')
  },
  // WHY an Undo did not work, in the active language: aiUndoCreated's `cause`
  // code → one of the ai.tool.undoWhy.* words (an unknown or missing code reads
  // the plain fallback). Never the raw error: wave 2 (dev 2026-10-28) showed an
  // English SDK message on a Georgian page. Visible until a retry works; the
  // Undo button reads "Try again" meanwhile.
  UndoFailure: {
    hideDeps: ['_aiUndoError'],
    hide: (el, s) =>
      !(s.created && s.root && s.root._aiUndoError && s.root._aiUndoError[s.created.id]),
    tag: 'p',
    margin: '0',
    padding: 'X Z 0 B',
    fontSize: 'Z1',
    lineHeight: '1.4',
    color: 'caption',
    overflowWrap: 'anywhere',
    UndoSaid: { tag: 'span', text: '{{ ai.tool.undoFailed | polyglot }}' },
    UndoWhy: {
      tag: 'span',
      // read behind a conditional — declared so the factory re-fires (FA515)
      textDeps: ['_aiUndoError'],
      text: (el, s) => {
        const e = s.created && s.root && s.root._aiUndoError && s.root._aiUndoError[s.created.id]
        if (!e) return ''
        // cause code → key, inline (self-contained); literal keys only
        const keys = {
          gone: 'ai.tool.undoWhy.gone',
          denied: 'ai.tool.undoWhy.denied',
          unavailable: 'ai.tool.undoWhy.unavailable',
          network: 'ai.tool.undoWhy.network'
        }
        const cause = String(e.cause || '')
        const key = Object.prototype.hasOwnProperty.call(keys, cause)
          ? keys[cause]
          : 'ai.tool.undoWhy.failed'
        let said = ''
        try {
          said = el.call('polyglot', key)
        } catch (_) {
          said = ''
        }
        return typeof said === 'string' && said ? ` ${said}` : ''
      }
    }
  },

  // Reasoning note — the model's narration before this step (what it streamed
  // between the previous tool and this one), snapped out of the answer bubble
  // by the transport. Always visible under the row header (not gated behind
  // the expand), quiet, italic — the "why" for the step.
  // Only on a step that RUNS (F3, dev 2026-10-29 W12c): those words are written
  // before the call, and a delete_ticket row read "I've deleted the task …"
  // while it waited for Run / Skip and after Skip. A step that waits, was
  // skipped or was closed by a new message shows its label alone; the note
  // comes in with Run, in the same change that takes Run / Skip away. A step
  // that never ran (`notRun`: its approval ran out, or its turn failed while
  // it waited) is a failed row with "did not run" under it, and no note.
  Note: {
    // Rule 66: the note is the model's words, not chrome. It comes in with
    // the person's own Run (the same change takes Run / Skip away) and leaves
    // when the answer takes its words (the transport clears the last step's
    // note). A kept box would leave a blank band under every step that shows
    // no note: one that waits, was skipped or closed, or has no note at all.
    // frank-allow FA813: the model's words come and go with the step's own change
    if: (el, s) =>
      !!s.note && !s.notRun && !['approval', 'denied', 'superseded'].includes(s.status),
    tag: 'p',
    margin: '0',
    padding: 'X Z Y2 B',
    fontSize: 'Z1',
    lineHeight: '1.4',
    fontStyle: 'italic',
    color: 'caption',
    whiteSpace: 'pre-wrap',
    text: (el, s) => s.note
  },

  // AI-CREATED WIDGET PREVIEW (tickets/opus.md "Per-workspace AI-CREATED
  // widgets"): a pending create_widget/update_widget renders the widget ITSELF
  // above Run / Skip — approving something you can SEE is a materially better
  // consent surface than approving a JSON diff, and the rendering primitive
  // already exists (the same WidgetRenderer that will draw it on the board, so
  // the preview cannot drift from the real thing).
  //
  // ADDITIVE + GATED: `if:` is false for every other tool and for every phase
  // that is not `approval`, so no existing row in any consumer changes shape.
  // `WidgetRenderer` is a workspace-shell component; in a host that does not
  // register it the bare key simply mounts nothing (the preview is absent, the
  // Run / Skip row is unaffected) — this row deliberately carries no import.
  // Values render as placeholders: a pending widget has no id yet, so the
  // renderer's own fetch guard no-ops and the preview shows STRUCTURE, which is
  // exactly what is being approved.
  WidgetPreview: {
    // Rule 66: the widget being approved, not chrome. It shows only while a
    // widget step waits on Run / Skip, and leaves with the same change that
    // takes Run / Skip away. A kept box would put a WidgetRenderer in every
    // tool row (four more elements, its state factory, two capability gates
    // of two el.call each, an onRender / onUpdate pair that calls
    // loadAiWidgetData) and a blank plate under each one.
    // frank-allow FA813: the widget being approved, only while its step waits on Run / Skip
    if: (el, s) => {
      if (s.status !== 'approval') return false
      const tool = String(s.tool || '')
      if (!/widget/i.test(tool)) return false
      const a = s.args
      const bag = Array.isArray(a) ? a[a.length - 1] : a && a.widget ? a.widget : a
      return !!(bag && bag.body && Array.isArray(bag.body.blocks) && bag.body.blocks.length)
    },
    flow: 'y',
    gap: 'Y',
    margin: '0 Z Y B',
    padding: 'Z',
    // Preview plate — same radius family as a card/tile (`radiusCard`), not
    // the retired spacing-letter radius (see the Head button's `round`
    // comment above for why `Z` is not "small A" here).
    round: 'radiusCard',
    background: 'overlay',

    Caption: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: '{{ ai.tool.widgetHint | polyglot }}'
    },

    WidgetRenderer: {
      state: (el, s) => {
        const a = s.args
        const bag = Array.isArray(a) ? a[a.length - 1] : a && a.widget ? a.widget : a
        const def = bag || {}
        return {
          // Deliberately id-LESS: `loadAiWidgetData` returns early without an
          // id, so an unapproved widget never issues a data read. The preview
          // is the STRUCTURE that is being approved — blocks, labels, layout —
          // with placeholder values.
          id: null,
          title: def.title || 'Widget',
          icon: def.icon || 'star',
          hue: def.hue || null,
          body: def.body || { blocks: [] },
          dataRecipe: def.dataRecipe || null,
          // The preview draws structure only — it never resolves the recipe
          // (no id → the renderer's fetch guard returns early), so this stamp
          // cannot be a way to see data. The REAL capability check happens
          // server-side on create and on every read.
          viewerCapability: { allowed: true, reason: null }
        }
      }
    }
  },

  Detail: {
    // read behind a conditional — declared so the factory re-fires (FA515;
    // see Head.OpenCreated)
    hideDeps: ['_aiToolOpen'],
    hide: (el, s) => !(s.root && s.root._aiToolOpen && s.root._aiToolOpen[s.id]),
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    padding: 'X Z Y B',

    // The call's ARGUMENTS as label / value rows, never raw JSON (live on dev
    // 2026-10-02: `"params": "<object:who,range>"` in a terminal). A name in
    // the person's words, in the active language (`ai.toolArg.*`; a name the
    // vocabulary does not hold reads humanized: "viewId" → "View id"); a text
    // as written; the server's previews (`<object:k1,k2>`, `<array:N>`) and a
    // real nested value readable — the keys as a list, an array as its count.
    // Flat, borderless: the overlay plate the terminal header uses.
    // A list with no row to show (every value empty, or an id) has no block.
    ToolArgs: {
      // Rule 66: the call's arguments, not chrome, inside the details the
      // person opened. The block changes only when the arguments do (they
      // normally come with the call's first frame); a kept box would show an
      // empty Input block.
      // frank-allow FA813: the details' own content, set by the call's arguments
      if: (el, s) => {
        const a = s.args && typeof s.args === 'object' ? s.args : null
        if (!a) return false
        // The rows' own rule (ToolArgsList below): a row needs a value, and
        // a database id or a uuid is no value a person reads.
        const isId = (str) =>
          /^[0-9a-f]{24}$/i.test(str) || /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(str)
        return Object.keys(a).some((k) => {
          const v = a[k]
          if (v == null) return false
          if (Array.isArray(v)) return true
          if (typeof v === 'object') return Object.keys(v).length > 0
          const str = String(v).trim()
          const obj = /^<object:(.*)>$/.exec(str)
          if (obj) return obj[1].split(',').some(Boolean)
          return str !== '' && !isId(str)
        })
      },
      flow: 'y',
      width: '100%',
      minWidth: '0',
      round: 'radiusControl',
      overflow: 'hidden',
      background: 'overlay',
      ToolArgsLabel: {
        padding: 'Y2 Z',
        color: 'caption',
        fontSize: 'Z2',
        fontWeight: '700',
        text: (el) => el.call('polyglot', 'ai.tool.input')
      },
      ToolArgsList: {
        tag: 'dl',
        margin: '0',
        padding: '0 Z Z',
        flow: 'y',
        gap: 'Y',
        minWidth: '0',
        children: (el, s) => {
          const args = s && s.args && typeof s.args === 'object' ? s.args : {}
          const tool = s && s.tool
          // THE ARGUMENT NAMES IN WORDS (dev 2026-10-30: a Georgian session's
          // step read "Ticket" — the tool's own key, in English): each name
          // in the person's words, in the active language — the row's one
          // map (scope.toolArgName), the one the collapsed hint reads too.
          // Never an id: rows show human labels, never a database id or a
          // uuid (the collapsed Hint keeps the same rule).
          const isId = (str) =>
            /^[0-9a-f]{24}$/i.test(str) ||
            /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(str)
          const items = (n) =>
            String(el.call('polyglot', 'ai.tool.items')).replace('{count}', String(n))
          const value = (v) => {
            if (v == null) return ''
            if (Array.isArray(v)) return items(v.length)
            if (typeof v === 'object') return Object.keys(v).join(', ')
            const str = String(v)
            if (isId(str.trim())) return ''
            const obj = /^<object:(.*)>$/.exec(str)
            if (obj) return obj[1].split(',').filter(Boolean).join(', ')
            const arr = /^<array:(\d+)>$/.exec(str)
            if (arr) return items(arr[1])
            return str.length > 200 ? str.slice(0, 200) + '…' : str
          }
          // An argument with no value (or only an id) has no row; the name is
          // the quiet line (caption ink, a step below) over its value (title
          // ink).
          return Object.keys(args)
            .map((k) => [k, value(args[k])])
            .filter(([, v]) => String(v).trim() !== '')
            .map(([k, v], i) => ({
              key: 'arg' + i,
              flow: 'y',
              gap: 'X',
              minWidth: '0',
              ToolArgName: {
                tag: 'dt',
                color: 'caption',
                fontSize: 'Y2',
                text: el.scope.toolArgName(el, k, tool)
              },
              ToolArgValue: {
                tag: 'dd',
                margin: '0',
                color: 'title',
                fontSize: 'Z',
                overflowWrap: 'anywhere',
                text: v
              }
            }))
        }
      }
    },
    ToolTerminal_1: {
      // Rule 66: the step's result, not chrome. It comes when the step ends,
      // as the last block of the details the person opened; its height is not
      // known before, so no box can be kept for it (a kept box would show an
      // empty terminal).
      // frank-allow FA813: the result comes with the step's end, last in the details
      if: (el, s) => !!s.summary,
      label: 'ai.tool.result',
      value: (el, s) => s.summary || ''
    }
  }
}

// ChatToolStepLabel — a step's words ALONE, for a surface that names a step
// without its row: the island's "Working: …" head and the approval bubble
// above a minimized bar (AiBubbleStack). It runs the SAME text factory as the
// row's Label — the server's label first (already in the turn's language),
// else the tool's own verb phrase — so a step reads identically wherever it
// is named. Primary text wraps; `oneLine` cuts it to one line instead, its
// whole text in the title (the overflow law).
export const ChatToolStepLabel = {
  tag: 'span',
  minWidth: '0',
  overflowWrap: 'anywhere',
  text: ChatToolRow.Head.Label.text,
  title: ChatToolRow.Head.Label.text,
  isOneLine: (el) => !!el.oneLine,
  '.isOneLine': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
}
